import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';

import tableIconUserShare from '../../../../assets/images/tabler-icon-user-share.svg';
import Notification from '../../../../components/Notification/Notification';
import ButtonWithIcon from '../../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import ReferAccessButtonTooltip from '../../../../components/shared/ReferAccessButtonTooltip/ReferAccessButtonTooltip';
import { capitalizeWords } from '../../../../helpers/commonHelper';
import { useReferralAccess } from '../../../../hooks/useReferralAccess';
import urls from '../../../../routing/urls';
import { selectCurrentOrganization } from '../../../../selectors/organizationSelectors';

const MatchingContactCard = ({ contact, matchingScore, matchingReason, opening }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const organization = useSelector(selectCurrentOrganization);
  const { blocked, message, tooltipMessage } = useReferralAccess(organization);

  const initials = contact.full_name
    ? contact.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : 'U';

  const handleReferClick = () => {
    if (blocked) {
      Notification.error(message || t('referralAccess.referDisabled'));
      return;
    }

    const referUrl = `${urls.refer}?refer_for=${opening.uuid}&c_id=${contact.user_contact_id}`;
    navigate(referUrl);
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-base font-medium flex-shrink-0">
          {initials}
        </div>

        <div className="flex-grow min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start gap-2 w-full">
            <div className="flex flex-col gap-1 flex-grow min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {capitalizeWords(contact.full_name)}
                </span>
                <span className="px-2 py-0.5 bg-[#D5F3DF] text-emerald-700 rounded-full text-xs font-medium whitespace-nowrap w-fit">
                  {t('matchScore', { score: Math.floor(matchingScore * 100) })}
                </span>
              </div>
              <p className="text-sm text-gray-600 truncate mb-0">
                {contact.job_title}
                {contact.company && ` @ ${contact.company}`}
              </p>
              {contact.location && (
                <div className="text-sm text-gray-500 truncate flex items-center gap-1">
                  <MapPin className="w-3 h-3 inline-block flex-shrink-0" />
                  {contact.location}
                </div>
              )}
              {matchingReason && (
                <div className="mt-2 rounded-md bg-purple-50 border border-purple-100 px-3 py-2">
                  <p className="text-xs font-semibold text-purple-700 mb-0.5">{t('matchingReason.title')}</p>
                  <p className="text-xs text-purple-600 leading-relaxed mb-0">{matchingReason}</p>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 mt-1 sm:mt-0">
              <ReferAccessButtonTooltip blocked={blocked} tooltipText={tooltipMessage}>
                <ButtonWithIcon
                  text={t('buttons.refer')}
                  img={tableIconUserShare}
                  imgClassName="btn-icon-36"
                  textClassName="btn-icon-text-tertiary-40"
                  variant="tertiary-36"
                  onClick={handleReferClick}
                  disabled={blocked}
                />
              </ReferAccessButtonTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingContactCard;
